import React, { useState, useMemo, JSX } from "react";
import "./UserDetails.css";
import { PlusCircleIcon } from "../../../../icons/reactIcons";
import StepIcon from "../../../../icons/svg-cons/StepIcon";
import Active24Icon from "../../../../icons/svg-cons/Active24Icon";
import NutritionIcon from "../../../../icons/svg-cons/NutritionIcon";
import SleepBedIcon from "../../../../icons/svg-cons/SleepBedIcon";
import MindFullIcon from "../../../../icons/svg-cons/MindFullIcon";
import ActivityModal from "../user-modal/ActivityModal";
import { ActivityData } from "../../../../apis/userApis";
import {
  addUserActivity,
  updateUserActivity,
} from "../../../../apis/userActivityApis";
import WaterGlassIcon from "../../../../icons/svg-cons/WaterGlassIcon";

/* ================= Props ================= */

interface UserActivityProps {
  activityData: ActivityData[];
  userId: string; // 👈 added (required for API)
}

/* ================= Icon Mapper ================= */

const iconMap: Record<string, JSX.Element> = {
  "steps.svg": <StepIcon />,
  "activeminutes.svg": <Active24Icon />,
  "nutrition.svg": <NutritionIcon />,
  "sleep.svg": <SleepBedIcon />,
  "water.svg": <WaterGlassIcon />,
  "mindfulness.svg": <MindFullIcon />,
};

const getActivityIcon = (logoName: string) => {
  return iconMap[logoName?.toLowerCase()] || <StepIcon />;
};

const UserActivity: React.FC<UserActivityProps> = ({
  activityData,
  userId,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityData | null>(
    null,
  );
  const [selectedOption, setSelectedOption] = useState("");
  const [loading, setLoading] = useState(false); // 👈 added

  /* ================= Derived Activity UI Data ================= */

  const processedActivities = useMemo(() => {
    return (
      activityData
        ?.slice()
        ?.sort(
          (a, b) => (a.activitySortOrder ?? 0) - (b.activitySortOrder ?? 0),
        )
        ?.map((activity) => {
          const percentage = 0;

          const isEmpty =
            activity.activityValues === null ||
            activity.activityValues === "" ||
            activity.activityValues === 0;

          let status = "";

          if (!isEmpty) {
            status =
              percentage >= 100
                ? "Achieved"
                : percentage >= 70
                  ? "In Progress"
                  : "Needs Attention";
          }

          return {
            ...activity,
            percentage,
            status,
          };
        }) || []
    );
  }, [activityData]);

  const completedCount = processedActivities.filter(
    (a) => a.status === "Achieved",
  ).length;

  const openModal = (activity: ActivityData) => {
    setSelectedActivity(activity);
    setSelectedOption("");
    setModalOpen(true);
  };

  /* ================= API INTEGRATION ================= */

  const handleUpdate = async () => {
    if (!selectedActivity || !selectedOption) return;

    try {
      setLoading(true);

      const isEmpty =
        selectedActivity.activityMappingId === null ||
        selectedActivity.activityMappingId === "";

      // ✅ CREATE
      if (isEmpty) {
        await addUserActivity(userId, {
          activityId: selectedActivity.activityId,
          activityValues: selectedOption,
        });
      }

      // ✅ UPDATE
      else {
        await updateUserActivity(userId, {
          activityMappingId: selectedActivity.activityMappingId, // using activityId
          activityValues: selectedOption,
        });
      }

      console.log(
        `Updated ${selectedActivity.activityName} with ${selectedOption}`,
      );

      setModalOpen(false);
      setSelectedActivity(null);
      setSelectedOption("");
    } catch (error) {
      console.error("Activity update failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="activity-container">
      <div className="activity-header">
        <h2>User Activity Progress</h2>
        <div className="activity-controls">
          <span className="completed">
            {completedCount}/{processedActivities.length} Completed
          </span>
          {/* <button className="today-btn">Today 📅</button> */}
        </div>
      </div>

      <div className="activity-grid">
        {processedActivities?.map((activity) => (
          <div key={activity.activityId} className="activity-card">
            <div className="card-header">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div className="card-header-icon">
                  {getActivityIcon(activity.activityLogoName)}
                </div>
                <div style={{ marginTop: "4px", textTransform: "capitalize" }}>
                  <h3>{activity.activityName}</h3>
                  <p className="desc">{activity.activityDescription}</p>
                </div>
              </div>

              <PlusCircleIcon
                fontSize={"25px"}
                style={{ cursor: "pointer" }}
                onClick={() => openModal(activity)}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="value">{activity.activityValues}</div>

              <div
                className={`status ${activity.status
                  .toLowerCase()
                  .replace(" ", "-")}`}
              >
                {activity.status}
              </div>
            </div>

            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min(activity.percentage, 100)}%`,
                }}
              ></div>
            </div>

            <div className="card-footer">
              <span>
                Goal: {activity.goal} {activity.goalUnit}
              </span>
              <span>{activity.percentage}%</span>
            </div>
          </div>
        ))}
      </div>

      <ActivityModal
        isOpen={modalOpen}
        title={selectedActivity?.activityName || ""}
        options={selectedActivity?.activityOption || []}
        selectedOption={selectedOption}
        onSelect={setSelectedOption}
        onClose={() => setModalOpen(false)}
        onUpdate={handleUpdate} // 👈 API integrated here
        loading={loading}
      />
    </div>
  );
};

export default UserActivity;
